import { useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'

interface SyncLog {
    id: number
    status: string
    modelsFound: number
    modelsAdded: number
    modelsRemoved: number
    modelsUpdated: number
    errorMessage: string | null
    startedAt: string
    completedAt: string | null
    triggeredBy: string
    provider: { id: number; name: string; displayName: string } | null
}

export function SyncHistoryTab() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const { data, isLoading } = useQuery({
        queryKey: ['ai-sync-logs'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/sync/logs', { params: { limit: 30 } })
            return res.data
        },
        enabled: isAuthenticated,
    })

    const logs: SyncLog[] = data?.logs || []

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold">Sync History</h2>
                <p className="text-sm text-muted-foreground">Log of all model sync operations</p>
            </div>

            {isLoading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
            ) : logs.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">No sync history yet</div>
            ) : (
                <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/30">
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Provider</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Found</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Added</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Removed</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Triggered</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log) => (
                                <>
                                    <tr key={log.id} className="border-b border-border/50 hover:bg-muted/20">
                                        <td className="py-2.5 px-4 whitespace-nowrap">
                                            {new Date(log.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })},{' '}
                                            {new Date(log.startedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="py-2.5 px-4">{log.provider?.displayName || 'All'}</td>
                                        <td className="py-2.5 px-4">
                                            {log.status === 'completed' && (
                                                <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                                                    <span className="mr-1">&#10003;</span>Completed
                                                </span>
                                            )}
                                            {log.status === 'failed' && (
                                                <span className="text-red-600 dark:text-red-400 text-xs font-medium">
                                                    <span className="mr-1">&#10007;</span>Failed
                                                </span>
                                            )}
                                            {log.status === 'running' && (
                                                <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">Running...</span>
                                            )}
                                            {log.status !== 'completed' && log.status !== 'failed' && log.status !== 'running' && (
                                                <span className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">
                                                    <span className="mr-1">&#9888;</span>Partial
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">{log.modelsFound}</td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsAdded > 0 ? <span className="text-green-600 dark:text-green-400">+{log.modelsAdded}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsRemoved > 0 ? <span className="text-red-600 dark:text-red-400">-{log.modelsRemoved}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsUpdated > 0 ? <span className="text-yellow-600 dark:text-yellow-400">~{log.modelsUpdated}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <Badge variant="outline" className="text-[10px] uppercase">
                                                {log.triggeredBy}
                                            </Badge>
                                        </td>
                                    </tr>
                                    {/* Error message row */}
                                    {log.errorMessage && (
                                        <tr key={`${log.id}-err`}>
                                            <td colSpan={8} className="px-4 py-2">
                                                <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-3 py-1.5 font-mono">
                                                    {log.errorMessage}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
